import type { MyFilesResource } from "@/features/my-files/myFilesSchemas";
import { z } from "zod";

export const MAX_CHAT_ATTACHMENTS = 10;

export type ChatAttachmentResourceType = "file" | "image" | "video";
export type ChatAttachmentStatus = "failed" | "ready" | "uploading";

export interface ChatAttachment {
  attachmentId: string;
  displayName: string;
  errorMessage?: string;
  resourceId: string | null;
  resourceType: ChatAttachmentResourceType;
  status: ChatAttachmentStatus;
}

export interface ChatAttachmentResourceRef {
  resource_id: string;
  resource_type: ChatAttachmentResourceType;
}

const readyChatAttachmentSchema = z.object({
  attachmentId: z.string().min(1),
  displayName: z.string().min(1),
  resourceId: z.string().min(1),
  resourceType: z.enum(["file", "image", "video"]),
  status: z.literal("ready"),
}).strict();

export function parseReadyChatAttachment(value: unknown): ChatAttachment | null {
  const parsed = readyChatAttachmentSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function resourceTypeForFile(file: Pick<File, "type">) {
  if (file.type.startsWith("image/")) return "image" as const;
  if (file.type.startsWith("video/")) return "video" as const;
  return "file" as const;
}

export function readyAttachmentFromResource(
  resource: Pick<
    MyFilesResource,
    "displayName" | "resourceId" | "resourceType"
  >,
): ChatAttachment {
  return {
    attachmentId: resource.resourceId,
    displayName: resource.displayName,
    resourceId: resource.resourceId,
    resourceType: resource.resourceType,
    status: "ready",
  };
}

export function mergeChatAttachments(
  current: ReadonlyArray<ChatAttachment>,
  candidates: ReadonlyArray<ChatAttachment>,
  limit = MAX_CHAT_ATTACHMENTS,
) {
  const merged = [...current];
  const resourceIds = new Set(
    current.flatMap((attachment) =>
      attachment.resourceId ? [attachment.resourceId] : [],
    ),
  );
  const attachmentIds = new Set(
    current.map((attachment) => attachment.attachmentId),
  );

  for (const candidate of candidates) {
    if (merged.length >= limit) break;
    if (attachmentIds.has(candidate.attachmentId)) continue;
    if (candidate.resourceId && resourceIds.has(candidate.resourceId)) continue;
    merged.push(candidate);
    attachmentIds.add(candidate.attachmentId);
    if (candidate.resourceId) resourceIds.add(candidate.resourceId);
  }

  return merged;
}

export function replaceChatAttachment(
  current: ReadonlyArray<ChatAttachment>,
  attachmentId: string,
  replacement: ChatAttachment,
) {
  const withoutTarget = current.filter(
    (attachment) => attachment.attachmentId !== attachmentId,
  );
  const duplicateIndex = replacement.resourceId
    ? withoutTarget.findIndex(
        (attachment) => attachment.resourceId === replacement.resourceId,
      )
    : -1;

  if (duplicateIndex >= 0) return withoutTarget;
  const targetIndex = current.findIndex(
    (attachment) => attachment.attachmentId === attachmentId,
  );
  if (targetIndex < 0) return current;

  const next = [...current];
  next[targetIndex] = replacement;
  return next;
}

export function readyChatResourceIds(
  attachments: ReadonlyArray<ChatAttachment>,
) {
  return readyChatResourceRefs(attachments).map(
    (resource) => resource.resource_id,
  );
}

export function readyChatResourceRefs(
  attachments: ReadonlyArray<ChatAttachment>,
): ReadonlyArray<ChatAttachmentResourceRef> {
  return attachments.flatMap((attachment) =>
    attachment.status === "ready" && attachment.resourceId
      ? [{
          resource_id: attachment.resourceId,
          resource_type: attachment.resourceType,
        }]
      : [],
  );
}

export function hasUnreadyChatAttachments(
  attachments: ReadonlyArray<ChatAttachment>,
) {
  return attachments.some((attachment) => attachment.status !== "ready");
}

/** Historical metadata is untrusted; keep only stable, typed cloud references. */
export function projectSentChatResources(value: unknown): ReadonlyArray<ChatAttachmentResourceRef> {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  return value.slice(0, MAX_CHAT_ATTACHMENTS).flatMap((candidate) => {
    const parsed = z.object({
      resource_id: z.string().min(1).max(200).regex(/^[a-zA-Z0-9_.:-]+$/u),
      resource_type: z.enum(["file", "image", "video"]),
    }).strict().safeParse(candidate);
    if (!parsed.success || seen.has(parsed.data.resource_id)) return [];
    seen.add(parsed.data.resource_id);
    return [parsed.data];
  });
}
